/**
 * Performance Monitoring Utilities
 * Tracks Core Web Vitals and other performance metrics
 */

// Track Core Web Vitals
export const reportWebVitals = (onPerfEntry) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    import('web-vitals').then(({ onCLS, onINP, onFCP, onLCP, onTTFB }) => {
      onCLS(onPerfEntry);
      onINP(onPerfEntry); // INP replaced FID in web-vitals v4+
      onFCP(onPerfEntry);
      onLCP(onPerfEntry);
      onTTFB(onPerfEntry);
    }).catch((error) => {
      console.warn('Web Vitals failed to load:', error);
    });
  }
};

// Performance marks for custom measurements
export const markPerformance = (name) => {
  if (window.performance && window.performance.mark) {
    window.performance.mark(name);
  }
};

export const measurePerformance = (name, startMark, endMark) => {
  if (window.performance && window.performance.measure) {
    try {
      window.performance.measure(name, startMark, endMark);
      const measure = window.performance.getEntriesByName(name)[0];
      return measure.duration;
    } catch (e) {
      console.warn('Performance measurement failed:', e);
      return null;
    }
  }
  return null;
};

// Log performance metrics to console (can be sent to analytics)
export const logPerformance = (metric) => {
  // In production, send to analytics service (Google Analytics, etc.)
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Performance] ${metric.name}:`, metric.value.toFixed(2), metric.unit || 'ms');
  }

  // Example: Send to Google Analytics
  if (window.gtag) {
    window.gtag('event', metric.name, {
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_id: metric.id,
      metric_value: metric.value,
      metric_delta: metric.delta,
    });
  }
};

// Monitor page load performance
export const monitorPageLoad = () => {
  if (window.performance && window.performance.timing) {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const timing = window.performance.timing;
        const loadTime = timing.loadEventEnd - timing.navigationStart;
        const domReadyTime = timing.domContentLoadedEventEnd - timing.navigationStart;
        const renderTime = timing.domComplete - timing.domLoading;

        logPerformance({ name: 'Page Load Time', value: loadTime });
        logPerformance({ name: 'DOM Ready Time', value: domReadyTime });
        logPerformance({ name: 'Render Time', value: renderTime });
      }, 0);
    });
  }
};

// Monitor resource loading
export const getResourceTiming = () => {
  if (!window.performance || !window.performance.getEntriesByType) {
    return [];
  }

  const resources = window.performance.getEntriesByType('resource');
  const summary = {};

  resources.forEach((resource) => {
    const type = resource.initiatorType;
    if (!summary[type]) {
      summary[type] = { count: 0, totalSize: 0, totalDuration: 0 };
    }
    summary[type].count++;
    summary[type].totalSize += resource.transferSize || 0;
    summary[type].totalDuration += resource.duration;
  });

  return summary;
};

// Check if image is in viewport (for lazy loading validation)
export const isInViewport = (element) => {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

// Preload critical resources
export const preloadResource = (href, as = 'image', type = null) => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = as;
  link.href = href;
  if (type) link.type = type;
  document.head.appendChild(link);
};

// Prefetch next page resources
export const prefetchResource = (href) => {
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = href;
  document.head.appendChild(link);
};

// Get performance score (0-100)
export const getPerformanceScore = async () => {
  try {
    const vitals = {};

    // Get Web Vitals (v5 API)
    const { onCLS, onINP, onLCP } = await import('web-vitals');

    await new Promise((resolve) => {
      let completed = 0;
      const checkComplete = () => {
        completed++;
        if (completed === 3) resolve();
      };

      onCLS((metric) => { vitals.cls = metric.value; checkComplete(); });
      onINP((metric) => { vitals.inp = metric.value; checkComplete(); }); // INP replaced FID
      onLCP((metric) => { vitals.lcp = metric.value; checkComplete(); });
    });

    // Calculate score based on Core Web Vitals thresholds
    const clsScore = vitals.cls < 0.1 ? 100 : vitals.cls < 0.25 ? 50 : 0;
    const inpScore = vitals.inp < 200 ? 100 : vitals.inp < 500 ? 50 : 0; // INP thresholds
    const lcpScore = vitals.lcp < 2500 ? 100 : vitals.lcp < 4000 ? 50 : 0;

    const totalScore = (clsScore + inpScore + lcpScore) / 3;

    return {
      score: Math.round(totalScore),
      metrics: vitals,
      rating: totalScore >= 90 ? 'good' : totalScore >= 50 ? 'needs-improvement' : 'poor'
    };
  } catch (error) {
    console.warn('Failed to calculate performance score:', error);
    return null;
  }
};

export default {
  reportWebVitals,
  markPerformance,
  measurePerformance,
  logPerformance,
  monitorPageLoad,
  getResourceTiming,
  isInViewport,
  preloadResource,
  prefetchResource,
  getPerformanceScore,
};
